/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import { Component, inject, OnChanges, OnDestroy, signal, SimpleChanges, input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { AuthService } from '../../../auth.service';
import { HistoryEvent } from '../../../models/logbook/event.interface';
import { LogbookService } from '../../logbook.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

import { TranslatePipe } from '@ngx-translate/core';
import { HistoryEventsComponent } from '../history-events/history-events.component';
import { CollapseComponent } from '../../../components/collapse/collapse.component';

const EVENT_LIMIT = 100;

@Component({
  selector: 'vitamui-common-operation-history-tab',
  templateUrl: './operation-history-tab.component.html',
  styleUrls: ['./operation-history-tab.component.scss'],
  imports: [MatProgressSpinner, CollapseComponent, HistoryEventsComponent, TranslatePipe],
})
export class OperationHistoryTabComponent implements OnChanges, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly logbookService = inject(LogbookService);
  private readonly route = inject(ActivatedRoute);

  readonly id = input<string>(undefined);
  readonly identifier = input<string>(undefined);
  readonly collectionName = input<string>(undefined);
  readonly filteringByIdentifier = input(true);

  events = signal<HistoryEvent[]>([]);
  loading = signal(false);

  private readonly isDestroyed$ = new Subject<void>();

  ngOnChanges(changes: SimpleChanges) {
    if (changes.hasOwnProperty('identifier') || changes.hasOwnProperty('collectionName')) {
      if (this.id() && this.collectionName()) {
        this.initEvent();
      }
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next();
  }

  initEvent() {
    this.loading.set(true);
    this.events.set([]);
    this.route.paramMap
      .pipe(
        map((paramMap) =>
          paramMap.get('tenantIdentifier') ? +paramMap.get('tenantIdentifier') : Number(this.authService.user.proofTenantIdentifier),
        ),
        switchMap((tenantIdentifier) =>
          this.logbookService.listOperationByIdAndCollectionName(this.id(), this.collectionName(), tenantIdentifier),
        ),
        takeUntil(this.isDestroyed$),
      )
      .subscribe({
        next: (results) => {
          this.events.set(
            results
              .filter((event) => {
                // FIXME: shouldn't we filter on API side?
                return this.filteringByIdentifier() ? this.filterByIdentifier(event) : true;
              })
              .slice(0, EVENT_LIMIT),
          );
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  filterByIdentifier(event: HistoryEvent): boolean {
    return event.obId && event.obId === this.identifier();
  }
}
